import type { PXNovaTelemetryEvent } from '@1js/midgard-types';
import type { IExtendedTelemetryItem } from '@microsoft/1ds-analytics-js';
import { getClientVersion, getLogicalRing, getSessionId } from 'owa-config';
import { getGuid } from 'owa-guid';
import { getAccountScopeUserSettings } from 'owa-session-store';
import { lazyLogAddinsTelemetryEvent } from 'owa-analytics';
import type { MailboxInfo } from 'owa-client-types';
import { errorThatWillCauseAlert } from 'owa-trace';
import type { TraceErrorObject, CoreAnalyticsOptions } from 'owa-analytics-types';

type TelemetryContextProps = {
    hostApp: string;
    hostAppRing: string;
    hostAppVersion: string;
    hostAppPlatform: string;
    complianceEnvironment: string;
    hostAppSessionCorrelationId: string;
    userId: string;
    tenantId: string;
    puid: string;
};

type Middleware = (telemetryItem: IExtendedTelemetryItem) => IExtendedTelemetryItem;

type AppLoggerProps = {
    ingestionKey: string;
    mailboxInfo: MailboxInfo;
    complianceEnvironment: string;
    platformType: string;
    middlewares?: Middleware[];
    analyticsOptions?: CoreAnalyticsOptions;
};

export class NovaLogger {
    private static loggerPropsMap: Map<string, AppLoggerProps> = new Map();

    /**
     * This service enables Nova components to log PX Telemetry events.
     * When calling this service this function is used to tell it which app name to associate with an ingestion key.
     * _Always_ make sure to call this function
     * before making calls to _logEvent_ in order to log the events correctly and when the _mailbox_ changes.
     * @param appName the name of the app calling the telemetry service
     * @param loggerProps an object containing properties such as ingestion key, mailbox info, compliance environment, platform type, and middlewares
     */
    public static initializeNovaLogger(appName: string, loggerProps: AppLoggerProps): void {
        NovaLogger.loggerPropsMap.set(appName, loggerProps);
    }

    /**
     * Log an event to a Kusto cluster specified by an ingestion key. In order for this function call to work you need to call
     * _initializeNovaLogger_ first to tell the service which ingestion token an app name should route events to.
     * @param event an object that contain the event data
     */
    public static logEvent(event: PXNovaTelemetryEvent): void {
        const { appName, telemetryItem } = event.data();
        const loggerProps = NovaLogger.loggerPropsMap.get(appName);

        if (!loggerProps || !loggerProps.ingestionKey) {
            const traceErrorObject: TraceErrorObject = new Error(
                'NovaLogger_NoIngestionKeyAssociated'
            );
            traceErrorObject.additionalInfo = {
                appName,
                message: `No ingestion key associated with app name: ${appName}. Make sure to call initializeNovaLogger before logging events.`,
            };
            errorThatWillCauseAlert('NovaLogger_NoIngestionKeyAssociated', traceErrorObject);
            return;
        }

        const {
            analyticsOptions,
            complianceEnvironment,
            ingestionKey,
            mailboxInfo,
            middlewares,
            platformType,
        } = loggerProps;
        const telemetryItemWithContextProps = NovaLogger.addHostAppContextProperties(
            telemetryItem,
            mailboxInfo,
            complianceEnvironment,
            platformType
        );
        const finalTelemetryItem = NovaLogger.executeMiddleware(
            telemetryItemWithContextProps,
            middlewares
        );
        lazyLogAddinsTelemetryEvent.importAndExecute(
            ingestionKey,
            finalTelemetryItem,
            analyticsOptions,
            mailboxInfo
        );
    }

    private static executeMiddleware(
        telemetryItem: IExtendedTelemetryItem,
        middlewares?: Middleware[]
    ): IExtendedTelemetryItem {
        return middlewares
            ? middlewares.reduce((item, middleware) => middleware(item), telemetryItem)
            : telemetryItem;
    }

    private static addHostAppContextProperties(
        telemetryItem: IExtendedTelemetryItem,
        mailboxInfo: MailboxInfo,
        complianceEnvironment?: string,
        platformType?: string
    ): IExtendedTelemetryItem {
        const hostAppContextProps = NovaLogger.getHostAppContextProps(
            mailboxInfo,
            complianceEnvironment,
            platformType
        );
        return {
            ...telemetryItem,
            data: {
                ...telemetryItem.data,
                ...hostAppContextProps,
            },
        };
    }

    private static getHostAppContextProps(
        mailboxInfo: MailboxInfo,
        complianceEnvironment = 'Unknown',
        hostAppPlatform = 'Web'
    ): TelemetryContextProps {
        const accountScopeUserSettings = getAccountScopeUserSettings(mailboxInfo);
        const {
            TenantGuid = '',
            UserPuid = '',
            ExternalDirectoryUserGuid = '',
        } = accountScopeUserSettings.SessionSettings || {};
        const tenantId = TenantGuid.toLowerCase();
        const hostAppSessionCorrelationId = getSessionId() || getGuid();
        const hostAppRing = getLogicalRing() || '';
        const hostAppVersion = getClientVersion();
        return {
            hostApp: 'OneOutlook',
            hostAppSessionCorrelationId,
            hostAppPlatform,
            hostAppRing,
            hostAppVersion,
            complianceEnvironment,
            userId: ExternalDirectoryUserGuid,
            tenantId,
            puid: UserPuid,
        };
    }
}
